import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function PinLoginPage() {
  const { clubSlug } = useParams()
  const navigate = useNavigate()
  const [club, setClub] = useState(null)
  const [fighters, setFighters] = useState([])
  const [selected, setSelected] = useState(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    async function load() {
      const { data: clubData } = await supabase
        .from('clubs').select('*').eq('slug', clubSlug).single()
      if (!clubData) { setLoading(false); return }
      setClub(clubData)
      const { data: fightersData } = await supabase
        .from('fighters').select('*')
        .eq('club_id', clubData.id)
        .not('pin_code', 'is', null)
        .order('first_name')
      setFighters(fightersData || [])
      setLoading(false)
    }
    load()
  }, [clubSlug])

  function handlePinDigit(digit) {
    if (pin.length >= 4) return
    const newPin = pin + digit
    setPin(newPin)
    setError('')
    if (newPin.length === 4) verifyPin(newPin)
  }

  function handlePinDelete() {
    setPin(p => p.slice(0, -1))
    setError('')
  }

  async function verifyPin(enteredPin) {
    if (!selected) return
    if (enteredPin === selected.pin_code) {
      // Store fighter session in localStorage
      localStorage.setItem('fighter_session', JSON.stringify({
        fighter_id: selected.id,
        club_id: club.id,
        club_slug: clubSlug,
        name: selected.first_name + ' ' + selected.last_name,
        expires: Date.now() + 7 * 24 * 60 * 60 * 1000 // 7 days
      }))
      navigate('/my-card')
    } else {
      setError('Wrong PIN. Try again.')
      setPin('')
    }
  }

  const filtered = fighters.filter(f =>
    !search || (f.first_name + ' ' + f.last_name).toLowerCase().includes(search.toLowerCase())
  )

  if (loading) return <Screen club={null}><p style={{ color: '#888', textAlign: 'center' }}>Loading...</p></Screen>
  if (!club) return <Screen club={null}><p style={{ color: '#c00', textAlign: 'center' }}>Club not found.</p></Screen>

  if (selected) {
    return (
      <Screen club={club}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#fff', margin: '0 auto 10px' }}>
            {selected.first_name[0]}{selected.last_name[0]}
          </div>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>{selected.first_name} {selected.last_name}</div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 4 }}>Enter your PIN</div>
        </div>

        {/* PIN dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginBottom: '2rem' }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              width: 18, height: 18, borderRadius: '50%',
              background: i < pin.length ? '#fff' : 'transparent',
              border: '2px solid rgba(255,255,255,0.5)',
              transition: 'background 0.15s'
            }} />
          ))}
        </div>

        {error && <div style={{ color: '#ff8080', fontSize: 12, textAlign: 'center', marginBottom: '1rem' }}>{error}</div>}

        {/* Number pad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, maxWidth: 240, margin: '0 auto' }}>
          {[1,2,3,4,5,6,7,8,9].map(n => (
            <button key={n} onClick={() => handlePinDigit(String(n))} style={{
              padding: '16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 20, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.1s'
            }}>{n}</button>
          ))}
          <button onClick={() => { setSelected(null); setPin(''); setError('') }} style={{
            padding: '16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', fontSize: 12,
            cursor: 'pointer', fontFamily: 'inherit'
          }}>Back</button>
          <button onClick={() => handlePinDigit('0')} style={{
            padding: '16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 20, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit'
          }}>0</button>
          <button onClick={handlePinDelete} style={{
            padding: '16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', fontSize: 18,
            cursor: 'pointer', fontFamily: 'inherit'
          }}>⌫</button>
        </div>
      </Screen>
    )
  }

  return (
    <Screen club={club}>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center', marginBottom: '1.25rem' }}>
        Tap your name to log in
      </div>
      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search your name..."
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)',
          color: '#fff', fontSize: 14, outline: 'none', marginBottom: 12,
          boxSizing: 'border-box'
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
        {filtered.length === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center', padding: '1rem' }}>
            No fighters found
          </div>
        )}
        {filtered.map(f => (
          <button key={f.id} onClick={() => { setSelected(f); setPin(''); setError('') }} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 12, padding: '12px 16px', cursor: 'pointer', textAlign: 'left',
            fontFamily: 'inherit', transition: 'background 0.15s', width: '100%'
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 14, color: '#fff'
            }}>
              {f.first_name[0]}{f.last_name[0]}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{f.first_name} {f.last_name}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{f.type === 'kids' ? 'Kids' : 'Adult'}</div>
            </div>
          </button>
        ))}
      </div>

      <div style={{ marginTop: '1.5rem', padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 8 }}>Have an email account?</div>
        <button onClick={() => navigate(`/join/${clubSlug}`)} style={{
          background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 8, padding: '7px 16px', color: 'rgba(255,255,255,0.6)',
          fontSize: 12, cursor: 'pointer', fontFamily: 'inherit'
        }}>
          Sign in with email
        </button>
      </div>
    </Screen>
  )
}

function Screen({ club, children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#0a1628 0%,#1a3a6a 50%,#0a1628 100%)',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🥋</div>
          {club ? (
            <>
              <div style={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>{club.name}</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 4 }}>Fighter login</div>
            </>
          ) : (
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>MyAthleteCard</div>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}
